
const AVATAR_COLORS = [
];

/**
 * Generates the background color of an initials based avatar.
 *
 * @param {string?} initials - The initials of the avatar.
 * @returns {string}
 */
export function getAvatarColor(initials: ?string) {
    let colorIndex = 0;


    // If initials are not provided, we use a default color
    if (!initials) {
        return `rgba(${AVATAR_COLORS[colorIndex]}`;
    }

    if (initials) {
        let nameHash = 0;

        for (const s of initials) {
            nameHash += s.codePointAt(0);
        }

        colorIndex = nameHash % AVATAR_COLORS.length;
    }

    return `rgba(${AVATAR_COLORS[colorIndex]}`;
}

/**
 * Generates initials for a simple string.
 *
 * @param {string?} s - The string to generate initials for.
 * @returns {string?}
 */
export function getInitials(s: ?string) {
    // We don't want to use the domain part of an email address, if it is one
    const initialsBasis = s ? s.split('@')[0] : '';

    // Extract words using regex - matches sequences of alphanumeric characters
    const wordsArray = initialsBasis.match(/[a-zA-Z0-9]+/g) || [];
    let initials = '';

    for (const w of wordsArray) {
        initials.length < 2 && (initials += w.substr(0, 1).toUpperCase());
    }

    return initials;
}

const participantColors = new Map(); // Track participant ID to color mapping
const dynamicAvatarColors = [ ...AVATAR_COLORS ]; // Dynamic array that can grow
const usedColorIndices = new Set(); // Track used color indices

/**
 * Generates a new random color in HSL format.
 *
 * @returns {string}
 */
function generateRandomColor() {
    const randomHue = Math.floor(Math.random() * 360);
    const hue = (randomHue + 137.508) % 360;
    const finalHue = Math.round(hue);
    const saturation = 39;
    const lightness = 30;

    // eslint-disable-next-line max-len
    return `hsl(${finalHue}, ${saturation}%, ${lightness}%)`;
}

/**
 * Gets the next available color, generating a new one if needed.
 *
 * @returns {string}
 */
function getNextAvailableColor() {
    // First, try to use existing colors from the original AVATAR_COLORS
    for (let i = 0; i < dynamicAvatarColors.length; i++) {
        if (!usedColorIndices.has(i)) {
            usedColorIndices.add(i);

            // Convert original AVATAR_COLORS format to rgba, keep HSL as is
            if (dynamicAvatarColors[i].includes(',') && !dynamicAvatarColors[i].includes('hsl')) {
                return `rgba(${dynamicAvatarColors[i]}, 0.4)`;
            }

            return dynamicAvatarColors[i];
        }
    }

    // If all existing colors are used, generate a new unique color
    const newColor = generateRandomColor();
    const newIndex = dynamicAvatarColors.length;

    dynamicAvatarColors.push(newColor);
    usedColorIndices.add(newIndex);

    return newColor;
}

/**
 * Generates a unique color for participants.
 *
 * @returns {string}
 */
export function getUniqueParticipantColor() {
    return getNextAvailableColor();
}

/**
 * Assigns a color to a participant if they don't already have one.
 *
 * @param {string} id - The participant ID to assign a color to.
 * @param {Object} participant - The participant object.
 * @returns {string|null} - The assigned color or null if participant already has a color.
 */
export function assignColorToParticipant(id, participant) {
    // const interfaceConfig = APP.store.getState()['features/base/config'].interfaceConfig;
    // const tileBackground = getBrandingColorPalette(interfaceConfig)?.tileBackground;

    // console.log(tileBackground, 'tileBackground assignColorToParticipant');
    const existingColor = participant?.color;

    // if (tileBackground !== 'multiple') {
    //     participantColors.set(id, tileBackground);

    //     return null;
    // }

    // If participant already has a color, don't assign a new one
    if (existingColor !== undefined) {
        // Update our tracking
        participantColors.set(id, existingColor);

        return null;
    }

    // Check if we already assigned a color to this participant
    if (participantColors.has(id)) {
        return participantColors.get(id);
    }

    // Assign a new unique color
    const newColor = getUniqueParticipantColor();

    participantColors.set(id, newColor);

    return newColor;
}

/**
 * Removes a participant's color from tracking when they leave.
 *
 * @param {string} id - The participant ID to remove.
 */
export function removeParticipantColor(id) {
    const color = participantColors.get(id);

    if (color) {
        participantColors.delete(id);

        // Find the color index and mark it as available for reuse
        const colorIndex = dynamicAvatarColors.findIndex(c => {
            // Handle original AVATAR_COLORS format (converted to rgba)
            if (c.includes(',') && !c.includes('hsl') && color.includes('rgba')) {
                return `rgba(${c}, 0.4)` === color;
            }


            // Handle HSL colors directly
            return c === color;
        });

        if (colorIndex !== -1) {
            usedColorIndices.delete(colorIndex);
        }
    }
}

/**
 * Gets the assigned color for a participant.
 *
 * @param {string} id - The participant ID.
 * @returns {string|undefined} - The assigned color or undefined if not found.
 */
export function getParticipantColor(id) {
    return participantColors.get(id);
}

/**
 * Gets information about the current color system state (for debugging).
 *
 * @returns {Object} - Color system information.
 */
export function getColorSystemInfo() {
    return {
        totalColors: dynamicAvatarColors.length,
        usedColors: usedColorIndices.size,
        availableColors: dynamicAvatarColors.length - usedColorIndices.size,
        participantCount: participantColors.size,
        colors: [ ...dynamicAvatarColors ],
        usedIndices: [ ...usedColorIndices ]
    };
}
